/**
 * One-time migration tool — Unit 1 Oct-2026 Paytime renumbering remap.
 * See PROMPT_FOR_EMERGENT.md brief. Super Admin only.
 *
 * Upload the mapping CSV (employee_code, employee_name, biometric_id,
 * device_serial, old_biometric_id, paytime_present_days_oct2026,
 * paytime_code_if_different). Preview first (dry-run, no writes), then
 * Run Fix to actually back up, remap, supersede stale Oct punches,
 * re-link U1-… punches and rebuild October attendance.
 */
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Fingerprint, Upload, AlertTriangle, ShieldCheck, Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function Unit1BiometricFix() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async (dryRun) => {
    if (!file) { toast.warning("Pehle CSV file choose karein"); return; }
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/biometric/unit1-fix/run?dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setReport(data);
      toast.success(dryRun ? "Preview ready" : "Fix applied — report ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="unit1-biometric-fix-page">
      <header className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
          <Fingerprint className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            One-time migration tool
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Unit 1 Biometric Fix — Oct 2026
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Paytime PC reinstall se Unit 1 (MSD1K) ke biometric IDs galat ho gaye the.
            Mapping CSV upload karke re-map, supersede aur October attendance rebuild karein.
            <b> Unit 2 (MSD2K) is tool se touch nahi hota.</b>
          </p>
        </div>
      </header>

      <div className="bg-white ring-1 ring-slate-200 rounded-lg p-4 space-y-3">
        <input
          type="file"
          accept=".csv"
          data-testid="unit1-fix-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => run(true)} disabled={busy || !file}
                  data-testid="unit1-fix-preview-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Preview (dry-run, no writes)
          </Button>
          <Button
            className="bg-red-700 hover:bg-red-800"
            disabled={busy || !file}
            data-testid="unit1-fix-run-btn"
            onClick={() => {
              if (window.confirm(
                "Ye REAL change karega: backup lega, 64 employees ka biometric_id set karega, " +
                "Oct 2026 purane-ID punches ko supersede karega, U1-… punches re-link karega, " +
                "aur October attendance rebuild karega. Continue?"
              )) run(false);
            }}
          >
            <ShieldCheck className="h-4 w-4 mr-1" />
            Run Fix (writes data)
          </Button>
        </div>
      </div>

      {report && <ReportView report={report} />}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    rose: "text-red-700 bg-red-50 ring-red-200",
    amber: "text-amber-700 bg-amber-50 ring-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-200",
    slate: "text-slate-700 bg-slate-50 ring-slate-200",
  }[tone] || "text-slate-700 bg-slate-50 ring-slate-200";
  return (
    <div className={`ring-1 rounded-lg p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function ReportView({ report: r }) {
  return (
    <div className="space-y-4" data-testid="unit1-fix-report">
      <div className="rounded-lg bg-slate-900 text-white p-3 text-xs flex flex-wrap gap-4">
        <span><b>Mode:</b> {r.dry_run ? "PREVIEW (dry-run, no writes)" : "EXECUTED (data changed)"}</span>
        <span><b>Cutoff:</b> {r.cutoff_date}</span>
        <span><b>Backup ID:</b> {r.backup_id || "—"}</span>
        <span><b>Attendance run:</b> {r.attendance_run_id || "—"} ({r.attendance_run_status || "—"})</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Rows in file" value={r.total_rows} tone="slate" />
        <Stat label="Not found" value={r.not_found.length} tone={r.not_found.length ? "rose" : "emerald"} />
        <Stat label="Duplicates cleared" value={r.duplicates_cleared.length} tone={r.duplicates_cleared.length ? "amber" : "emerald"} />
        <Stat label="Big diffs (>1 day)" value={r.big_differences.length} tone={r.big_differences.length ? "amber" : "emerald"} />
        <Stat label="Still unmatched (MSD1K Oct)" value={r.still_unmatched_msd1k_oct.length} tone={r.still_unmatched_msd1k_oct.length ? "amber" : "emerald"} />
      </div>

      {r.not_found.length > 0 && (
        <Section title="Employee codes NOT found" icon={<AlertTriangle className="h-4 w-4 text-red-600" />}>
          <ul className="text-xs space-y-1">
            {r.not_found.map((n, i) => (
              <li key={i}><code className="font-mono">{n.employee_code}</code> — {n.name} {n.error && `(${n.error})`}</li>
            ))}
          </ul>
        </Section>
      )}

      {r.duplicates_cleared.length > 0 && (
        <Section title="Duplicate holders cleared">
          <ul className="text-xs space-y-1">
            {r.duplicates_cleared.map((d, i) => (
              <li key={i}>
                <code className="font-mono">{d.biometric_id}</code> was on{" "}
                <b>{d.cleared_from_employee_code}</b> {d.cleared_from_name} — cleared, now belongs to <b>{d.now_belongs_to}</b>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {r.within_file_conflicts.length > 0 && (
        <Section title="⚠ Conflicts WITHIN the file (needs manual review)">
          <ul className="text-xs space-y-1">
            {r.within_file_conflicts.map((c, i) => (
              <li key={i}><code>{c.biometric_id}</code>: {c.employee_code_a} vs {c.employee_code_b}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={`Mapping table (${r.table.length} rows)`}>
        <div className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-1 pr-3">Employee Code</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Biometric ID set</th>
                <th className="py-1 pr-3">Oct punches matched</th>
                <th className="py-1 pr-3">Superseded old punches</th>
                <th className="py-1 pr-3">Oct present (Bravura)</th>
                <th className="py-1 pr-3">Oct present (Paytime)</th>
                <th className="py-1 pr-3">Difference</th>
              </tr>
            </thead>
            <tbody>
              {r.table.map((row, i) => (
                <tr key={i} className={`border-b border-slate-100 ${
                  row.difference !== null && Math.abs(row.difference) > 1 ? "bg-amber-50" : ""
                }`}>
                  <td className="py-1 pr-3 font-mono">{row.employee_code}</td>
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3 font-mono">{row.biometric_id_set}</td>
                  <td className="py-1 pr-3">{row.oct_punches_matched}</td>
                  <td className="py-1 pr-3">{row.superseded_old_punches}</td>
                  <td className="py-1 pr-3">{row.oct_present_days_bravura ?? "—"}</td>
                  <td className="py-1 pr-3">{row.paytime_present_days_oct2026 ?? "—"}</td>
                  <td className="py-1 pr-3 font-semibold">{row.difference ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {r.big_differences.length > 0 && (
        <Section title="Big differences (> 1 day) — detail">
          <ul className="text-xs space-y-2">
            {r.big_differences.map((b, i) => (
              <li key={i} className="border-b border-slate-100 pb-2">
                <b>{b.employee_code}</b> {b.name}: Bravura {b.oct_present_days_bravura} vs
                Paytime {b.paytime_present_days_oct2026} (Δ {b.difference})
                <div className="text-slate-500">
                  Absent/anomaly dates: {b.absent_or_anomaly_dates_in_bravura?.join(", ") || "—"}
                </div>
                <div className="text-slate-400 italic">{b.likely_cause}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {r.still_unmatched_msd1k_oct.length > 0 && (
        <Section title="Still-unmatched MSD1K punch_ids (October)">
          <ul className="text-xs space-y-1">
            {r.still_unmatched_msd1k_oct.map((u, i) => (
              <li key={i}><code className="font-mono">{u.punch_id}</code> — {u.count}× ({u.first_date} → {u.last_date})</li>
            ))}
          </ul>
        </Section>
      )}

      {r.other_employees_on_plain_numeric_ids_with_oct_msd1k_punches.length > 0 && (
        <Section title="Other employees still on a plain numeric ID (not touched — FYI only)">
          <ul className="text-xs space-y-1">
            {r.other_employees_on_plain_numeric_ids_with_oct_msd1k_punches.map((o, i) => (
              <li key={i}>
                <code className="font-mono">{o.employee_code}</code> {o.name} — ID <code>{o.biometric_id}</code>,
                {" "}{o.oct_msd1k_punches} Oct MSD1K punches
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white ring-1 ring-slate-200 rounded-lg p-4">
      <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}
